import { FC } from 'react';

import { useFormik } from 'formik';
import { values } from 'remeda';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';

import { CasinoForm, validationSchema } from './form';
import { replaceEmptyStringsWithNull } from './utils';

import { CheckBoxAutocomplete } from 'v2/components/autocomplete/checkBoxAutocomplete/CheckBoxAutocomplete';
import { FileSelector } from 'v2/components/fileSystem/fileSelector/FileSelector';
import { CasinoBonusMarksEnum } from 'v2/constants';
import { PromoPackTypeEnum } from 'v2/types/CasinoModel';

interface CasinoFormDialogProps {
  title: string;
  isOpen: boolean;
  onClose: VoidFunction;
  initialValues: CasinoForm;
  isSubmitting: boolean;
  onSubmit: (values: CasinoForm, resetForm: VoidFunction) => void;
}

export const CasinoFormDialog: FC<CasinoFormDialogProps> = ({
  title,
  isOpen,
  onClose,
  initialValues,
  isSubmitting,
  onSubmit,
}) => {
  const formik = useFormik<CasinoForm>({
    initialValues,
    validationSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values, { resetForm }) => {
      const processedValues = replaceEmptyStringsWithNull(values);

      onSubmit(processedValues, resetForm);
    },
    enableReinitialize: true,
  });

  const handleSelectChange = (field: keyof CasinoForm) => (v: unknown) => {
    formik.setFieldValue(field, v);
  };

  const handleSelectImage = (field: keyof CasinoForm) => (value: string) => {
    formik.setFieldValue(field, value);
  };

  const handlePromoPackTypeChange = (_: React.ChangeEvent<HTMLInputElement>, value: string) => {
    if (value === PromoPackTypeEnum.STANDARD) {
      formik.setFieldValue('benefits', {
        promoPackType: PromoPackTypeEnum.STANDARD,
        freeSpinsCount: initialValues.benefits.freeSpinsCount,
        depositPercentage: initialValues.benefits.depositPercentage,
        customPromo: null,
      });
    } else {
      formik.setFieldValue('benefits', {
        promoPackType: PromoPackTypeEnum.CUSTOM,
        freeSpinsCount: null,
        depositPercentage: null,
        customPromo: initialValues.benefits.customPromo,
      });
    }
  };

  const handleClose = () => {
    onClose();
    formik.resetForm();
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>

      <DialogContent>
        <form id="casinoForm" className="form with-top-space" onSubmit={formik.handleSubmit}>
          <TextField
            name="name"
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label="Name"
            required
          />

          <TextField
            name="shortPromoText"
            value={formik.values.shortPromoText}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label="Short Promo Text"
            rows={1}
            multiline
            required
          />

          <TextField
            name="fullPromoText"
            value={formik.values.fullPromoText}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label="Full Promo Text"
            rows={3}
            multiline
            required
          />

          <div className="form-group">
            <TextField
              name="link"
              value={formik.values.link}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label="Link"
              required
              fullWidth
            />

            <TextField
              name="ymId"
              value={formik.values.ymId}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label="Yandex Metrica Id"
              required
              fullWidth
            />
          </div>

          <div className="form-group">
            <TextField
              name="promoCode"
              value={formik.values.promoCode}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label="Promo Code"
              required
              fullWidth
            />
          </div>

          <CheckBoxAutocomplete
            label="Marks"
            value={formik.values.marks}
            options={values(CasinoBonusMarksEnum)}
            onChange={handleSelectChange('marks')}
            required
          />

          <FormControl error={typeof formik.errors.benefits === 'string'}>
            <FormLabel>Promo Pack Type</FormLabel>
            <RadioGroup
              row
              name="benefits.promoPackType"
              value={formik.values.benefits.promoPackType}
              onChange={handlePromoPackTypeChange}
            >
              <FormControlLabel value={PromoPackTypeEnum.STANDARD} control={<Radio />} label="Стандартный" />
              <FormControlLabel value={PromoPackTypeEnum.CUSTOM} control={<Radio />} label="Кастомный" />
            </RadioGroup>
          </FormControl>

          {formik.values.benefits.promoPackType === PromoPackTypeEnum.STANDARD && (
            <>
              <div className="form-group">
                <TextField
                  name="benefits.freeSpinsCount"
                  value={formik.values.benefits.freeSpinsCount ?? ''}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  label="Free Spins Count"
                  type="number"
                  fullWidth
                  inputProps={{ min: 1 }}
                  helperText={
                    typeof formik.errors.benefits === 'object' && 'freeSpinsCount' in formik.errors.benefits
                      ? formik.errors.benefits.freeSpinsCount
                      : undefined
                  }
                  error={
                    typeof formik.errors.benefits === 'string' ||
                    (typeof formik.errors.benefits === 'object' &&
                      'freeSpinsCount' in formik.errors.benefits &&
                      !!formik.errors.benefits.freeSpinsCount)
                  }
                />
                <TextField
                  name="benefits.depositPercentage"
                  value={formik.values.benefits.depositPercentage ?? ''}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  label="Deposit Percentage"
                  type="number"
                  fullWidth
                  inputProps={{ min: 1 }}
                  helperText={
                    typeof formik.errors.benefits === 'object' && 'depositPercentage' in formik.errors.benefits
                      ? formik.errors.benefits.depositPercentage
                      : undefined
                  }
                  error={
                    typeof formik.errors.benefits === 'string' ||
                    (typeof formik.errors.benefits === 'object' &&
                      'depositPercentage' in formik.errors.benefits &&
                      !!formik.errors.benefits.depositPercentage)
                  }
                />
              </div>
              {typeof formik.errors.benefits === 'string' && (
                <FormHelperText error>{formik.errors.benefits}</FormHelperText>
              )}
            </>
          )}

          {formik.values.benefits.promoPackType === PromoPackTypeEnum.CUSTOM && (
            <TextField
              name="benefits.customPromo"
              value={formik.values.benefits.customPromo ?? ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label="Custom Promo"
              required
              inputProps={{ maxLength: 48 }}
              helperText={
                typeof formik.errors.benefits === 'object' && 'customPromo' in formik.errors.benefits
                  ? formik.errors.benefits.customPromo
                  : undefined
              }
              error={
                typeof formik.errors.benefits === 'object' &&
                'customPromo' in formik.errors.benefits &&
                !!formik.errors.benefits.customPromo
              }
            />
          )}

          <TextField
            name="winNotificationText"
            value={formik.values.winNotificationText}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label="Win Notification Text"
            required
            error={!!formik.errors.winNotificationText}
            helperText={formik.errors.winNotificationText}
          />

          <div className="form-group">
            <FileSelector
              value={formik.values.solidLogoId}
              label="Casino Logo (Solid)"
              onSelect={handleSelectImage('solidLogoId')}
              error={formik.errors.solidLogoId}
              required
            />

            <FileSelector
              value={formik.values.transparentLogoId}
              label="Casino Logo (Transparent)"
              onSelect={handleSelectImage('transparentLogoId')}
              error={formik.errors.transparentLogoId}
              required
            />
          </div>

          <div className="form-group">
            <FileSelector
              value={formik.values.logoVerticalId ?? ''}
              label="Vertical Logo"
              onSelect={handleSelectImage('logoVerticalId')}
              error={formik.errors.logoVerticalId}
            />

            <FileSelector
              value={formik.values.logoSquareId ?? ''}
              label="Square Logo"
              onSelect={handleSelectImage('logoSquareId')}
              error={formik.errors.logoSquareId}
            />
          </div>
        </form>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="error" variant="outlined">
          Cancel
        </Button>

        <Button form="casinoForm" type="submit" variant="contained" loading={isSubmitting}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
